/**
 * Platform Roadmap — editable Kanban (super_admin).
 * Database is the source of truth. Markdown export is a snapshot.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Icons } from '../common/Icons';
import { roadmapService } from '../../services/api';
import { parseMarkdown } from '../../utils/parseMarkdown';
import './RoadmapView.css';

const LANES = [
  { key: 'now', label: 'Now' },
  { key: 'next', label: 'Next' },
  { key: 'waiting', label: 'Waiting' },
  { key: 'parked', label: 'Parked' },
  { key: 'backlog', label: 'Backlog' },
];

const KINDS = ['gig', 'wait', 'parked_gig', 'hygiene', 'tenant_work'];
const TIERS = ['light', 'standard', 'heavy', 'mixed'];
const MATURITIES = ['live', 'partial', 'scoped', 'blocked'];
const ALL_COLUMNS = [...LANES.map((l) => l.key), 'live'];

const emptyCard = () => ({
  slug: '',
  title: '',
  kind: 'gig',
  column: 'backlog',
  tier: 'standard',
  summary: '',
  primary_doc: '',
  primary_doc_text: '',
  unblock: '',
  domain: '',
});

function docFileName(path) {
  if (!path) return null;
  const parts = String(path).split('/');
  return parts[parts.length - 1];
}

function kindLabel(kind) {
  return String(kind || '').replace(/_/g, ' ');
}

const RoadmapView = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [pathAhead, setPathAhead] = useState('');
  const [pathDraft, setPathDraft] = useState('');
  const [editingPath, setEditingPath] = useState(false);
  const [domains, setDomains] = useState([]);
  const [columns, setColumns] = useState({});
  const [liveOpen, setLiveOpen] = useState(false);

  const [drawerItem, setDrawerItem] = useState(null);
  const [drawerDraft, setDrawerDraft] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState(emptyCard());
  const [domainEdit, setDomainEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [docItem, setDocItem] = useState(null);
  const [docName, setDocName] = useState('');
  const [docText, setDocText] = useState('');
  const [docTab, setDocTab] = useState('markdown');
  const [docLoading, setDocLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await roadmapService.getBoard();
      const data = res?.data || {};
      setPathAhead(data.path_ahead || '');
      setPathDraft(data.path_ahead || '');
      setDomains(data.domains || []);
      setColumns(data.columns || {});
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load roadmap');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(null), 4000);
  };

  const moveCard = async (item, column, sortOrder) => {
    if (item.kind === 'hygiene' && (column === 'now' || column === 'next')) {
      setError('Hygiene cards cannot sit in Now or Next.');
      return;
    }
    const nowCards = columns.now || [];
    if (column === 'now' && nowCards.some((c) => c.id !== item.id)) {
      setError('Only one card can sit in Now. Move the current Now card first.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = { column };
      if (sortOrder != null) body.sort_order = sortOrder;
      await roadmapService.moveItem(item.id, body);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to move card');
    } finally {
      setSaving(false);
    }
  };

  const savePath = async () => {
    setSaving(true);
    try {
      await roadmapService.updatePathAhead(pathDraft);
      setPathAhead(pathDraft);
      setEditingPath(false);
      flash('Path ahead saved.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save path ahead');
    } finally {
      setSaving(false);
    }
  };

  const saveDrawer = async () => {
    if (!drawerItem || !drawerDraft) return;
    setSaving(true);
    try {
      await roadmapService.updateItem(drawerItem.id, {
        title: drawerDraft.title,
        kind: drawerDraft.kind,
        tier: drawerDraft.tier || null,
        summary: drawerDraft.summary,
        primary_doc: drawerDraft.primary_doc,
        unblock: drawerDraft.unblock,
        domain: drawerDraft.domain,
        slug: drawerDraft.slug,
      });
      setDrawerItem(null);
      setDrawerDraft(null);
      await load();
      flash('Card updated.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update card');
    } finally {
      setSaving(false);
    }
  };

  const openDoc = async (item) => {
    setDocItem(item);
    setDocName(item.primary_doc || `${item.slug}.md`);
    setDocText('');
    setDocTab(item.has_primary_doc ? 'preview' : 'markdown');
    setDocLoading(true);
    setError(null);
    try {
      const res = await roadmapService.getItem(item.id);
      setDocName(res.data.primary_doc || item.primary_doc || `${item.slug}.md`);
      setDocText(res.data.primary_doc_text || '');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load document');
    } finally {
      setDocLoading(false);
    }
  };

  const saveDoc = async () => {
    if (!docItem) return;
    setSaving(true);
    try {
      await roadmapService.updateItem(docItem.id, {
        primary_doc: docName,
        primary_doc_text: docText,
      });
      setDocItem(null);
      await load();
      flash('Document saved.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const createCard = async () => {
    setSaving(true);
    try {
      await roadmapService.createItem(createDraft);
      setCreating(false);
      setCreateDraft(emptyCard());
      await load();
      flash('Card created.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create card');
    } finally {
      setSaving(false);
    }
  };

  const removeCard = async (item) => {
    if (!window.confirm(`Delete empty Backlog card "${item.title}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await roadmapService.deleteItem(item.id);
      setDrawerItem(null);
      setDrawerDraft(null);
      await load();
      flash('Card deleted.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete card');
    } finally {
      setSaving(false);
    }
  };

  const archiveCard = async (item) => {
    if (!window.confirm(`Archive "${item.title}" to Live? Closed cards keep their document.`)) return;
    setSaving(true);
    setError(null);
    try {
      await roadmapService.moveItem(item.id, { column: 'live' });
      setDrawerItem(null);
      setDrawerDraft(null);
      await load();
      flash('Card archived to Live.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to archive card');
    } finally {
      setSaving(false);
    }
  };

  const saveDomain = async () => {
    if (!domainEdit) return;
    setSaving(true);
    try {
      await roadmapService.updateDomain(domainEdit.slug, {
        maturity: domainEdit.maturity,
        notes: domainEdit.notes,
      });
      setDomainEdit(null);
      await load();
      flash('Domain updated.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update domain');
    } finally {
      setSaving(false);
    }
  };

  const onDragStart = (event, item) => {
    event.dataTransfer.setData('text/plain', String(item.id));
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDropLane = (event, column) => {
    event.preventDefault();
    const id = parseInt(event.dataTransfer.getData('text/plain'), 10);
    if (!Number.isFinite(id)) return;
    const item = ALL_COLUMNS.flatMap((k) => columns[k] || []).find((c) => c.id === id);
    if (!item || item.column === column) return;
    moveCard(item, column);
  };

  const renderCard = (item) => (
    <article
      key={item.id}
      className="roadmap-card"
      draggable
      onDragStart={(e) => onDragStart(e, item)}
      onClick={() => { setDrawerItem(item); setDrawerDraft({ ...item }); }}
    >
      <div className="roadmap-card-meta">
        <span className={`roadmap-chip kind-${item.kind}`}>{kindLabel(item.kind)}</span>
        {item.tier && <span className="roadmap-chip tier">{item.tier}</span>}
      </div>
      <h3>{item.title}</h3>
      {item.summary && <p>{item.summary}</p>}
      <button
        type="button"
        className={`roadmap-doc ${item.has_primary_doc ? 'has-doc' : ''}`}
        onClick={(e) => { e.stopPropagation(); openDoc(item); }}
      >
        {item.primary_doc ? docFileName(item.primary_doc) : 'Add document'}
      </button>
      <div className="roadmap-card-actions" onClick={(e) => e.stopPropagation()}>
        <label>
          Move
          <select
            value={item.column}
            onChange={(e) => moveCard(item, e.target.value)}
            disabled={saving}
          >
            {ALL_COLUMNS.map((col) => (
              <option key={col} value={col}>{col}</option>
            ))}
          </select>
        </label>
      </div>
    </article>
  );

  if (loading) {
    return (
      <div className="empty-state">
        <Icons.Loader size={48} className="spin" />
        <p>Loading roadmap…</p>
      </div>
    );
  }

  return (
    <div className="roadmap-view">
      {error && (
        <div className="alert alert-error mb-3">
          <Icons.AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}
      {message && (
        <div className="alert alert-success mb-3">
          <Icons.CheckCircle size={18} />
          <span>{message}</span>
        </div>
      )}

      <section className="roadmap-path">
        <div className="roadmap-path-head">
          <h2>Path ahead</h2>
          {!editingPath ? (
            <button className="btn btn-secondary btn-sm" onClick={() => setEditingPath(true)}>
              <Icons.Edit size={14} /> Edit
            </button>
          ) : (
            <div className="roadmap-path-actions">
              <button className="btn btn-primary btn-sm" onClick={savePath} disabled={saving}>Save</button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setEditingPath(false); setPathDraft(pathAhead); }}>Cancel</button>
            </div>
          )}
        </div>
        {editingPath ? (
          <textarea
            className="form-input"
            rows={3}
            value={pathDraft}
            onChange={(e) => setPathDraft(e.target.value)}
          />
        ) : (
          <p>{pathAhead || 'No path-ahead blurb yet.'}</p>
        )}
      </section>

      <section className="roadmap-domains">
        {domains.map((d) => (
          <button
            key={d.slug}
            type="button"
            className={`roadmap-domain maturity-${d.maturity}`}
            onClick={() => setDomainEdit({ ...d })}
            title={d.notes || d.label}
          >
            <span className="roadmap-domain-label">{d.label}</span>
            <span className="roadmap-domain-maturity">{d.maturity}</span>
          </button>
        ))}
      </section>

      <div className="roadmap-toolbar">
        <button className="btn btn-primary btn-sm" onClick={() => { setCreating(true); setCreateDraft(emptyCard()); }}>
          <Icons.Plus size={14} /> New card
        </button>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={saving}>
          <Icons.RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="roadmap-board">
        {LANES.map((lane) => (
          <section
            key={lane.key}
            className={`roadmap-lane lane-${lane.key}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDropLane(e, lane.key)}
          >
            <header>
              <h2>{lane.label}</h2>
              <span>{(columns[lane.key] || []).length}</span>
            </header>
            <div className="roadmap-lane-body">
              {(columns[lane.key] || []).map(renderCard)}
            </div>
          </section>
        ))}
      </div>

      <section className="roadmap-live">
        <button type="button" className="roadmap-live-toggle" onClick={() => setLiveOpen((v) => !v)}>
          <Icons.ChevronDown size={16} style={{ transform: liveOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
          Live ({(columns.live || []).length})
        </button>
        {liveOpen && (
          <div
            className="roadmap-live-list"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDropLane(e, 'live')}
          >
            {(columns.live || []).map(renderCard)}
            {(columns.live || []).length === 0 && <p className="text-muted">No archived cards. Shipped and rejected work both land here.</p>}
          </div>
        )}
      </section>

      {drawerDraft && (
        <div className="modal-overlay" onClick={() => { setDrawerItem(null); setDrawerDraft(null); }}>
          <div className="modal roadmap-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit card</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => { setDrawerItem(null); setDrawerDraft(null); }}>
                <Icons.X size={16} />
              </button>
            </div>
            <div className="modal-body">
              <Field label="Title" value={drawerDraft.title} onChange={(v) => setDrawerDraft({ ...drawerDraft, title: v })} />
              <Field label="Slug" value={drawerDraft.slug} onChange={(v) => setDrawerDraft({ ...drawerDraft, slug: v })} />
              <Select label="Kind" value={drawerDraft.kind} options={KINDS} onChange={(v) => setDrawerDraft({ ...drawerDraft, kind: v })} />
              <Select label="Tier" value={drawerDraft.tier || ''} options={['', ...TIERS]} onChange={(v) => setDrawerDraft({ ...drawerDraft, tier: v })} />
              <Field label="Domain" value={drawerDraft.domain || ''} onChange={(v) => setDrawerDraft({ ...drawerDraft, domain: v })} />
              <Field label="Document name" value={drawerDraft.primary_doc || ''} onChange={(v) => setDrawerDraft({ ...drawerDraft, primary_doc: v })} />
              <div className="form-group">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => openDoc(drawerItem)}>
                  <Icons.FileText size={14} /> Open document
                </button>
              </div>
              <Field label="Unblock" value={drawerDraft.unblock || ''} onChange={(v) => setDrawerDraft({ ...drawerDraft, unblock: v })} />
              <div className="form-group">
                <label className="form-label">Summary</label>
                <textarea className="form-input" rows={4} value={drawerDraft.summary || ''} onChange={(e) => setDrawerDraft({ ...drawerDraft, summary: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              {drawerItem.column !== 'live' && (drawerItem.has_primary_doc || drawerItem.column !== 'backlog') && (
                <button className="btn btn-secondary btn-sm" onClick={() => archiveCard(drawerItem)} disabled={saving}>
                  Archive to Live
                </button>
              )}
              {drawerItem.column === 'backlog' && !drawerItem.has_primary_doc && (
                <button className="btn btn-danger btn-sm" onClick={() => removeCard(drawerItem)} disabled={saving}>
                  Delete
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => { setDrawerItem(null); setDrawerDraft(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={saveDrawer} disabled={saving}>Save</button>
            </div>
          </div>
        </div>
      )}

      {creating && (
        <div className="modal-overlay" onClick={() => setCreating(false)}>
          <div className="modal roadmap-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New card</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setCreating(false)}><Icons.X size={16} /></button>
            </div>
            <div className="modal-body">
              <Field label="Title" value={createDraft.title} onChange={(v) => setCreateDraft({ ...createDraft, title: v })} />
              <Field label="Slug" value={createDraft.slug} onChange={(v) => setCreateDraft({ ...createDraft, slug: v })} />
              <Select label="Kind" value={createDraft.kind} options={KINDS} onChange={(v) => setCreateDraft({ ...createDraft, kind: v })} />
              <Select label="Column" value={createDraft.column} options={ALL_COLUMNS} onChange={(v) => setCreateDraft({ ...createDraft, column: v })} />
              <Select label="Tier" value={createDraft.tier} options={TIERS} onChange={(v) => setCreateDraft({ ...createDraft, tier: v })} />
              <Field label="Domain" value={createDraft.domain} onChange={(v) => setCreateDraft({ ...createDraft, domain: v })} />
              <Field label="Document name" value={createDraft.primary_doc} onChange={(v) => setCreateDraft({ ...createDraft, primary_doc: v })} />
              <div className="form-group">
                <label className="form-label">Summary</label>
                <textarea className="form-input" rows={3} value={createDraft.summary} onChange={(e) => setCreateDraft({ ...createDraft, summary: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Document</label>
                <textarea
                  className="form-input roadmap-doc-editor"
                  rows={10}
                  value={createDraft.primary_doc_text}
                  onChange={(e) => setCreateDraft({ ...createDraft, primary_doc_text: e.target.value })}
                  placeholder="Compose the primary document here (markdown). Stored on the card."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCreating(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={createCard} disabled={saving || !createDraft.title || !createDraft.slug}>Create</button>
            </div>
          </div>
        </div>
      )}

      {docItem && (
        <div className="modal-overlay" onClick={() => setDocItem(null)}>
          <div className="modal roadmap-doc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{docItem.title}</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setDocItem(null)}><Icons.X size={16} /></button>
            </div>
            <div className="modal-body">
              {docLoading ? (
                <p className="text-muted">Loading document…</p>
              ) : (
                <>
                  <Field label="Document name" value={docName} onChange={setDocName} />
                  <div className="form-group">
                    <div className="roadmap-doc-tabs" role="tablist">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={docTab === 'markdown'}
                        className={`roadmap-doc-tab ${docTab === 'markdown' ? 'active' : ''}`}
                        onClick={() => setDocTab('markdown')}
                      >
                        Markdown
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={docTab === 'preview'}
                        className={`roadmap-doc-tab ${docTab === 'preview' ? 'active' : ''}`}
                        onClick={() => setDocTab('preview')}
                      >
                        Preview
                      </button>
                    </div>
                    {docTab === 'markdown' ? (
                      <textarea
                        className="form-input roadmap-doc-editor"
                        rows={18}
                        value={docText}
                        onChange={(e) => setDocText(e.target.value)}
                        placeholder="Compose or edit the primary document (markdown)."
                      />
                    ) : docText ? (
                      <article
                        className="roadmap-doc-preview"
                        dangerouslySetInnerHTML={{ __html: parseMarkdown(docText).html }}
                      />
                    ) : (
                      <article className="roadmap-doc-preview">
                        <p className="text-muted">Nothing to preview yet. Switch to Markdown to compose.</p>
                      </article>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDocItem(null)}>Close</button>
              <button className="btn btn-primary" onClick={saveDoc} disabled={saving || docLoading}>Save document</button>
            </div>
          </div>
        </div>
      )}

      {domainEdit && (
        <div className="modal-overlay" onClick={() => setDomainEdit(null)}>
          <div className="modal roadmap-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{domainEdit.label}</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setDomainEdit(null)}><Icons.X size={16} /></button>
            </div>
            <div className="modal-body">
              <Select label="Maturity" value={domainEdit.maturity} options={MATURITIES} onChange={(v) => setDomainEdit({ ...domainEdit, maturity: v })} />
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-input" rows={4} value={domainEdit.notes || ''} onChange={(e) => setDomainEdit({ ...domainEdit, notes: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDomainEdit(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveDomain} disabled={saving}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function Field({ label, value, onChange }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input className="form-input" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Select({ label, value, options, onChange }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <select className="form-input" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((opt) => (
          <option key={opt || '(none)'} value={opt}>{opt || '—'}</option>
        ))}
      </select>
    </div>
  );
}

export default RoadmapView;
